import type { InquiryListRequest } from '@ghp/api-client'
import { uuidV7Schema } from '@ghp/domain'
import { z } from 'zod'

import { urlCount, urlText } from '../../../lib/search-params'

/**
 * Le modèle de l'écran « Archives » : le schéma des `search params`, et la **couture** vers la
 * requête serveur. Hors de la route pour que le composant d'écran reste du markup, et parce que
 * `validateSearch` doit être chargé avant l'écran (cf. `app/router.ts`).
 *
 * ⚠️ Même discipline que « Demandes » et « Export clients » : l'URL **est** l'état — une période
 * consultée se partage en copiant l'adresse —, chaque champ porte un `.catch` (M21 appliqué à
 * l'URL), et **rien n'est filtré en mémoire** (D24).
 *
 * ⚠️ **Cet écran n'a pas de filtre d'état** : `status` n'est pas dans le schéma d'URL parce que
 * ce n'est pas un choix de l'utilisateur mais la **définition** de l'écran — les archives sont
 * `status = 'archived'` (M3), et rien d'autre. Le mettre dans l'URL laisserait écrire
 * `/archives?status=submitted`, c'est-à-dire un écran « Archives » qui montre ce qui reste à
 * valider.
 *
 * ⚠️ **Le rail « Période » de la maquette validée n'est pas servi, et il ne peut pas l'être en
 * calculant sur la page reçue.** `inquiryListRowSchema` ne porte **aucune date** — la ligne
 * servie est `id, reference, caseReference, status, address, team` : les mois et leurs compteurs
 * n'ont rien d'où se dériver. Deux autres pistes ont été écartées : `created_at`/`updated_at`
 * (M5) existent en base mais ne sont pas servis, et `updated_at` n'est de toute façon pas une
 * date de clôture — la moindre écriture le déplace ; l'horodatage de l'uuid v7 (M7) est un
 * identifiant, pas une donnée métier. Élargir le contrat est un autre lot ; d'ici là l'écran dit
 * que la période n'est pas réglable, plutôt que de dérouler des mois inventés.
 *
 * ⚠️ Le « client » de la maquette n'est pas non plus dans ce tableau : faute de `Lot`/`Client`,
 * le rail filtre par **équipe** — le mot « client » ne s'affiche donc nulle part sur cet écran,
 * plutôt que de coiffer une liste d'équipes.
 */

export const archiveSearchSchema = z.object({
  q: urlText(''),
  /**
   * Le rail gauche : l'**identifiant** d'équipe, jamais son nom. `JL·TB` est un champ libre que
   * renommer l'équipe change, et comparer des libellés ferait disparaître la moitié des lignes au
   * premier renommage (M22). `'all'` est une valeur du filtre, pas une absence.
   *
   * ⚠️ **Le schéma du serveur, repris mot pour mot** (`teamIdSchema`,
   * `apps/api/src/inquiries/inquiry-list-query.ts`) : une valeur malformée retombe sur `'all'`,
   * un uuid v7 bien formé passe et **filtre pour de bon, même si aucune équipe ne le porte**.
   * Le reprendre ici fait dire à `search.team` exactement ce que le serveur appliquera, donc
   * l'écran peut s'y fier pour savoir s'il est filtré. Le déduire de l'annuaire des équipes, lui,
   * fait annoncer « toutes les équipes » au-dessus d'un tableau filtré dès que `GET /teams`
   * échoue ou n'a pas encore répondu (revue de #199).
   */
  team: z
    .union([z.literal('all'), uuidV7Schema])
    .default('all')
    .catch('all'),
  offset: urlCount(0),
})

export type ArchiveSearch = z.infer<typeof archiveSearchSchema>

/** Les valeurs par défaut du schéma, partagées entre l'URL et la remise à zéro du rail. */
export const ARCHIVE_DEFAULTS: ArchiveSearch = archiveSearchSchema.parse({})

/** Combien de lignes une page demande — même densité que les autres tableaux du bureau. */
export const ARCHIVE_PAGE_SIZE = 50

/**
 * **La couture** : l'état d'URL de l'écran vers ce que l'API accepte (D21).
 *
 * C'est ici — et nulle part ailleurs — que vit l'écart entre ce que le prototype montre et ce que
 * le modèle porte. Un filtre absent de cette fonction est un filtre que le serveur n'applique
 * pas.
 *
 * ⚠️ `status: 'archived'` est posé ici, pas dans l'URL : c'est le sujet de l'écran, et aucune
 * route d'API nouvelle n'est nécessaire — `GET /inquiries/back-office` sert déjà ce filtre.
 */
export function toArchiveListQuery(search: ArchiveSearch): InquiryListRequest {
  return {
    search: search.q.trim(),
    status: 'archived',
    // Le paramètre d'URL s'appelle `team`, celui du serveur `teamId` : c'est un identifiant, et
    // le contrat le nomme pour ce qu'il est (M22).
    teamId: search.team,
    limit: ARCHIVE_PAGE_SIZE,
    offset: search.offset,
  }
}
